import type { AIDecompositionResult } from "@/types";
import { mockDecompose } from "./mock-ai";

// client for the flask backend.
// the one real endpoint is /api/ai/decompose which proxies to gpt-5.5
// via openrouter (key stays server-side). if the backend is unreachable
// or errors, we fall back to a deterministic mock so the demo never breaks.

const AI_ENDPOINT = "/api/ai/decompose";

export interface DecomposeOptions {
  text: string;
  signal?: AbortSignal;
}

export async function decomposeIdea({ text, signal }: DecomposeOptions): Promise<AIDecompositionResult> {
  if (!text.trim()) throw new Error("Empty input");

  try {
    const res = await fetch(AI_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text }),
      signal,
    });

    if (!res.ok) {
      throw new Error(`AI endpoint returned ${res.status}`);
    }

    const data = await res.json();

    // backend returns either a parsed gpt-5.5 response or { error, fallback }
    if (data && data.nodes && data.nodes.length > 0) {
      return data as AIDecompositionResult;
    }
    if (data && data.error) {
      console.warn("[ai] backend error, using mock:", data.error);
      return mockDecompose(text);
    }
    throw new Error("Malformed AI response");
  } catch (err: any) {
    if (err.name === "AbortError") throw err;
    // network error, dev mode (no flask), or backend down — graceful fallback
    console.warn("[ai] decompose failed, using mock:", err?.message ?? err);
    return mockDecompose(text);
  }
}

export async function checkHealth(): Promise<{ ok: boolean; ai?: boolean; model?: string }> {
  try {
    const res = await fetch("/api/health", { signal: AbortSignal.timeout(2000) });
    if (!res.ok) return { ok: false };
    const data = await res.json();
    return { ok: !!data.ok, ai: !!data.ai, model: data.model };
  } catch {
    return { ok: false };
  }
}
